import { Injectable, inject } from '@angular/core';
import SparkMD5 from 'spark-md5';
import { Observable, of, forkJoin, catchError, map, switchMap } from 'rxjs';
import { ApiService } from './api.service';
import { UserStateService } from './user-state.service';
import { ThemeGalleryInfo, ColorInfo, ThemeCollectionInfo, ThemeSetInfo, ThemeableCategory, ThemeableProductInfo, ProductThemesData, ThemeLocaleInfo } from '../models';

/**
 * Page Preview Info - structure for theme preview images
 * Used by removeThemePreview AMF method
 */
export interface PagePreviewInfo {
  id: string | number;
  themeId: string | number;
  serverName: string;
  pathStructureId: string | number;
  serverId: string | number;
  width: number;
  height: number;
  sizes: Array<{
    size: string;
    width: number;
    height: number;
  }>;
}

/**
 * Paged result for theme collections listing.
 */
export interface PagedCollectionsResult {
  items: ThemeCollectionInfo[];
  currentPage: number;
  totalPages: number;
  total: number;
}

/**
 * Image Info - returned by uploadThemePreview
 */
export interface ImageInfo {
  id: string | number;
  serverId: string | number;
  pathStructureId: string | number;
  serverName: string;
  width: number;
  height: number;
  sizes: Array<{
    size: string;
    width: number;
    height: number;
  }>;
}

/**
 * Theme Service - for Designer feature
 *
 * REST API endpoints under /themes/*
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly api = inject(ApiService);
  private readonly userState = inject(UserStateService);

  private resolveThemeSetCollectionId(set: any): string | null {
    const rawCollectionId =
      set?.collectionId ??
      set?.collection_id ??
      set?.themeCollectionId ??
      set?.theme_collection_id ??
      set?.themeCollection?.id ??
      set?.themeCollection ??
      null;

    if (rawCollectionId === null || rawCollectionId === undefined || rawCollectionId === '') {
      return null;
    }

    return String(rawCollectionId);
  }

  /**
   * Get theme galleries (categories like "Flores", "Abstrato")
   * GET /themes/galleries
   */
  getGalleries(): Observable<ThemeGalleryInfo[]> {
    return this.api.get<any>('/themes/galleries').pipe(
      map(res => {
        if (Array.isArray(res)) return res;
        if (res?.items) return res.items;
        return [];
      }),
      catchError(error => {
        console.error('[ThemeService] getGalleries error:', error);
        return of([]);
      })
    );
  }

  /**
   * Get available colors for themes
   * GET /themes/colors
   */
  getColors(): Observable<ColorInfo[]> {
    return this.api.get<any>('/themes/colors').pipe(
      map(res => {
        if (Array.isArray(res)) return res;
        if (res?.items) return res.items;
        return [];
      }),
      catchError(error => {
        console.error('[ThemeService] getColors error:', error);
        return of([]);
      })
    );
  }

  /**
   * Get a single page of theme collections with pagination metadata.
   * GET /themes/collections?page=N&limit=100
   */
  getThemesCollectionsPage(page: number = 1, limit: number = 100): Observable<PagedCollectionsResult> {
    return this.api.get<any>('/themes/collections?page=' + page + '&limit=' + limit).pipe(
      map(res => {
        const items: ThemeCollectionInfo[] = Array.isArray(res) ? res : (res?.items || []);
        const pagination = res?.pagination || {};
        return {
          items,
          currentPage: Number(pagination.current_page) || page,
          totalPages: Number(pagination.total_pages) || 1,
          total: Number(pagination.total) || items.length
        };
      }),
      catchError(error => {
        console.error('[ThemeService] getThemesCollectionsPage error:', error);
        return of({ items: [], currentPage: 1, totalPages: 1, total: 0 });
      })
    );
  }

  /**
   * Get ALL theme collections (across all pages).
   *
   * Used for client-side search (cached) so the search term matches across the full
   * set, not just the currently-browsed page. The API caps `limit` at 1000 server-side
   * so a single request usually returns everything; when total exceeds 1000 the
   * remaining pages are fetched in parallel and concatenated.
   *
   * GET /themes/collections?page=N&limit=1000
   */
  getThemesCollections(): Observable<ThemeCollectionInfo[]> {
    const pageSize = 1000;
    return this.api.get<any>('/themes/collections?page=1&limit=' + pageSize).pipe(
      switchMap(firstRes => {
        const firstItems: ThemeCollectionInfo[] = Array.isArray(firstRes)
          ? firstRes
          : (firstRes?.items || []);
        const totalPages = Number(firstRes?.pagination?.total_pages) || 1;

        if (totalPages <= 1) {
          return of(firstItems);
        }

        // Fetch remaining pages in parallel
        const additionalRequests: Observable<ThemeCollectionInfo[]>[] = [];
        for (let page = 2; page <= totalPages; page++) {
          additionalRequests.push(
            this.api.get<any>('/themes/collections?page=' + page + '&limit=' + pageSize).pipe(
              map(res => Array.isArray(res) ? res : (res?.items || []))
            )
          );
        }

        return forkJoin(additionalRequests).pipe(
          map(pages => {
            const all = [...firstItems];
            for (const items of pages) {
              all.push(...items);
            }
            return all;
          })
        );
      }),
      catchError(error => {
        console.error('[ThemeService] getThemesCollections error:', error);
        return of([]);
      })
    );
  }

  /**
   * Get theme sets for a specific collection
   * GET /themes/collections/:collectionId/sets/status/:statusFilter
   *
   * @param collectionId - The collection ID
   * @param statusFilter - Status filter: 0 = all themes, 2 = published only
   *                       Note: Server uses 0 as "no filter" (show all), not "drafts only"
   */
  getThemeSets(collectionId: number, statusFilter: number = 0, page: number = 1, limit: number = 100): Observable<{ items: ThemeSetInfo[]; currentPage: number; totalPages: number; total: number }> {
    console.log('[ThemeService] getThemeSets:', { collectionId, statusFilter, page, limit });
    return this.api.get<any>('/themes/collections/' + collectionId + '/sets/status/' + statusFilter, { params: { page, limit } }).pipe(
      map(res => {
        if (Array.isArray(res)) return { items: res, currentPage: 1, totalPages: 1, total: res.length };
        const items = res?.items || [];
        const pagination = res?.pagination || {};
        return {
          items,
          currentPage: Number(pagination.current_page) || page,
          totalPages: Number(pagination.total_pages) || 1,
          total: Number(pagination.total) || items.length
        };
      }),
      catchError(error => {
        console.error('[ThemeService] getThemeSets error:', error);
        return of({ items: [], currentPage: 1, totalPages: 1, total: 0 });
      })
    );
  }

  /**
   * Insert a new theme variant (new color and/or locale combination)
   * POST /themes/collections
   *
   * @param themeSetId - The theme set ID to add a variant to
   * @param colorId - The color ID for the new variant
   * @param locale - The locale for the new variant (e.g., 'pt_PT', 'es_ES')
   * @returns The newly created theme info or error
   */
  insertTheme(themeSetId: number, colorId: string | number, locale: string): Observable<any> {
    console.log('[ThemeService] insertTheme:', { themeSetId, colorId, locale });
    return this.api.post<any>('/themes/collections', { themeSetId, colorId, locale }).pipe(
      catchError(error => {
        console.error('[ThemeService] insertTheme error:', error);
        throw error;
      })
    );
  }

  /**
   * Create a new theme collection
   * POST /themes/collections
   * Body: { galleryId, restrict, translations: [{parameter, locale, translation}] }
   * Response: { themeCollection: {...} } on HTTP 201
   */
  createCollection(body: any): Observable<any> {
    return this.api.post<any>('/themes/collections', body).pipe(
      map(result => {
        if (result && typeof result === 'object' && 'themeCollection' in result) {
          return result.themeCollection;
        }
        return result;
      }),
      catchError(error => {
        console.error('[ThemeService] createCollection error:', error);
        throw error;
      })
    );
  }

  /**
   * Update a theme collection
   * PUT /themes/collections/:collectionId
   */
  updateCollection(collectionId: number, body: any): Observable<any> {
    return this.api.put<any>('/themes/collections/' + collectionId, body).pipe(
      map(result => {
        if (result && typeof result === 'object' && 'themeCollection' in result) {
          return result.themeCollection;
        }
        return result;
      }),
      catchError(error => {
        console.error('[ThemeService] updateCollection error:', error);
        throw error;
      })
    );
  }

  /**
   * Delete a theme collection
   * DELETE /themes/collections/:collectionId
   */
  deleteCollection(collectionId: number): Observable<any> {
    return this.api.delete<any>('/themes/collections/' + collectionId).pipe(
      catchError(error => {
        console.error('[ThemeService] deleteCollection error:', error);
        throw error;
      })
    );
  }

  /**
   * Remove a theme preview image
   * DELETE /upload/themes/:themeId/previews/:previewId
   *
   * @param preview - The PagePreviewInfo object to remove
   * @returns Observable<number> - 1 on success
   */
  removeThemePreview(preview: PagePreviewInfo): Observable<number> {
    console.log('[ThemeService] removeThemePreview:', preview);
    return this.api.delete<number>('/upload/themes/' + preview.themeId + '/previews/' + preview.id).pipe(
      catchError(error => {
        console.error('[ThemeService] removeThemePreview error:', error);
        throw error;
      })
    );
  }

  /**
   * Upload a theme preview image (multipart/form-data).
   *
   * CREATE: POST /upload/themes/{themeId}/previews
   * UPDATE: POST /upload/themes/{themeId}/previews/{previewId}
   *
   * Fields: file (real file upload WITH filename), mainImage (0|1), md5 (hex hash).
   * When mainImage=1, the create endpoint upserts (replaces existing main image).
   */
  uploadThemePreview(
    file: File | Blob,
    themeId: string | number,
    mainImage: number = 1,
    themePreviewId?: string | number
  ): Observable<ImageInfo> {
    // Compute MD5 of the file bytes
    const formData = new FormData();
    formData.append('file', file, (file as File).name || 'preview.png');
    formData.append('mainImage', String(mainImage));

    // Build URL: with previewId for update, without for create (upserts when mainImage=1)
    const url = themePreviewId && themePreviewId !== 0 && themePreviewId !== '0'
      ? '/upload/themes/' + themeId + '/previews/' + themePreviewId
      : '/upload/themes/' + themeId + '/previews';

    console.log('[ThemeService] uploadThemePreview:', { themeId, mainImage, previewId: themePreviewId || 'CREATE', url });

    // Read the file to compute MD5, then upload
    return new Observable<ImageInfo>(subscriber => {
      const reader = new FileReader();
      reader.onload = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const uint8Array = new Uint8Array(arrayBuffer);
        const md5 = this.calculateMd5(uint8Array);
        formData.append('md5', md5);

        this.api.upload<ImageInfo>(url, formData).pipe(
          catchError(error => {
            console.error('[ThemeService] uploadThemePreview error:', error);
            throw error;
          })
        ).subscribe(subscriber);
      };
      reader.onerror = () => subscriber.error(new Error('Failed to read file for MD5'));
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Upload theme project bytes (multipart/form-data).
   *
   * UPDATE: POST /upload/themes-bytes/{themeId}
   * CREATE: POST /upload/themes-bytes (with themeSetId + colorId)
   *
   * Single-shot upload: chunk = entire file, fileOffset = 0, chunkMd5 = md5.
   */
  uploadThemeBytes(
    themeId: string | number,
    colorId: string | number,
    color: string | number,
    bytes: ArrayBuffer | Uint8Array,
    locale?: string | null
  ): Observable<boolean> {
    const uint8Array = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes;
    const md5 = this.calculateMd5(uint8Array);

    // chunk must be a plain base64 string (NOT a Blob/file upload).
    // PHP reads form fields from $_POST; a Blob with filename goes to $_FILES instead.
    let binary = '';
    for (let i = 0; i < uint8Array.byteLength; i++) {
      binary += String.fromCharCode(uint8Array[i]);
    }
    const base64Chunk = btoa(binary);

    const formData = new FormData();
    formData.append('chunk', base64Chunk);
    formData.append('fileOffset', '0');
    formData.append('fileSize', String(uint8Array.byteLength));
    formData.append('md5', md5);
    formData.append('chunkMd5', md5);
    formData.append('colorId', String(colorId));
    if (color) formData.append('color', String(color));
    if (locale) formData.append('locale', locale);

    console.log('[ThemeService] uploadThemeBytes (multipart):', { themeId, colorId, color, locale, fileSize: uint8Array.length, md5 });

    return this.api.upload<any>('/upload/themes-bytes/' + themeId, formData).pipe(
      map(response => {
        if (response === '1' || response === true || (response && typeof response === 'object')) return true;
        console.error('[ThemeService] uploadThemeBytes returned error:', response);
        throw new Error(String(response));
      }),
      catchError(error => {
        console.error('[ThemeService] uploadThemeBytes error:', error);
        throw error;
      })
    );
  }

  /**
   * Create a new theme AND upload its file content in one request (multipart/form-data).
   * POST /upload/themes-bytes (no ID in URL = create mode)
   *
   * @param themeSetId - Parent theme set ID
   * @param colorId - Color ID for the new theme
   * @param bytes - Theme project JSON bytes
   * @param color - Optional color hex (e.g. "0xff0000")
   * @param locale - Optional locale code (e.g. "pt_PT")
   */
  createTheme(
    themeSetId: string | number,
    colorId: string | number,
    bytes: ArrayBuffer | Uint8Array,
    color?: string | number | null,
    locale?: string | null
  ): Observable<any> {
    const uint8Array = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes;
    const md5 = this.calculateMd5(uint8Array);

    let binary = '';
    for (let i = 0; i < uint8Array.byteLength; i++) {
      binary += String.fromCharCode(uint8Array[i]);
    }
    const base64Chunk = btoa(binary);

    const formData = new FormData();
    formData.append('chunk', base64Chunk);
    formData.append('fileOffset', '0');
    formData.append('fileSize', String(uint8Array.byteLength));
    formData.append('md5', md5);
    formData.append('chunkMd5', md5);
    formData.append('themeSetId', String(themeSetId));
    formData.append('colorId', String(colorId));
    if (color) formData.append('color', String(color));
    if (locale) formData.append('locale', locale);

    console.log('[ThemeService] createTheme (multipart):', { themeSetId, colorId, color, locale, fileSize: uint8Array.byteLength, md5 });

    return this.api.upload<any>('/upload/themes-bytes', formData).pipe(
      map(response => {
        // API returns { complete: true, theme: {...} } — unwrap to the theme object
        if (response && typeof response === 'object' && 'theme' in response) {
          return response.theme;
        }
        return response;
      }),
      catchError(error => {
        console.error('[ThemeService] createTheme error:', error);
        throw error;
      })
    );
  }

  /**
   * Get themeable product categories
   * GET /themes/categories
   *
   * @returns Observable<ThemeableCategory[]> - Array of product categories
   */
  getThemeableCategories(): Observable<ThemeableCategory[]> {
    return this.api.get<any>('/themes/categories').pipe(
      map(res => {
        if (Array.isArray(res)) return res;
        if (res?.items) return res.items;
        return [];
      }),
      catchError(error => {
        console.error('[ThemeService] getThemeableCategories error:', error);
        return of([]);
      })
    );
  }

  /**
   * Get themeable products for a category
   * GET /themes/categories/:categoryId/products
   *
   * @param categoryId - The category ID to get products for
   * @returns Observable<ThemeableProductInfo[]> - Array of products in the category
   */
  getThemeableProducts(categoryId: string | number): Observable<ThemeableProductInfo[]> {
    return this.api.get<any>('/themes/categories/' + categoryId + '/products').pipe(
      map(res => {
        if (Array.isArray(res)) return res;
        if (res?.items) return res.items;
        return [];
      }),
      catchError(error => {
        console.error('[ThemeService] getThemeableProducts error:', error);
        return of([]);
      })
    );
  }

  /**
   * Publish a theme set
   * PUT /themes/sets/:themeSetId/publish
   *
   * Copies theme .json to approved folder (server-side) and updates status from 0 (draft) to 2 (published)
   *
   * @param themeSetId - The theme set ID to publish
   * @returns Observable<number> - 1 on success
   */
  publishThemeSet(themeSetId: string | number): Observable<any> {
    console.log('[ThemeService] publishThemeSet:', themeSetId);
    return this.api.put<any>('/themes/sets/' + themeSetId + '/publish').pipe(
      catchError(error => {
        console.error('[ThemeService] publishThemeSet error:', error);
        throw error;
      })
    );
  }

  /**
   * Save theme set with product associations
   * POST /themes/sets (create new, id=0)
   * PUT /themes/sets/:themeSetId (update existing)
   *
   * The products array in themeSetInfo has `selected: boolean` for each product
   *
   * @param themeSetInfo - The theme set info with updated product associations
   * @param collectionId - The theme collection ID (required for new themes, optional for updates)
   * @returns Observable<ThemeSetInfo | string> - The saved theme set info, or error string
   */
  saveThemeSet(themeSetInfo: ThemeSetInfo, collectionId?: number | string): Observable<ThemeSetInfo | string> {
    const isNew = !themeSetInfo.id || themeSetInfo.id === '0';
    // Resolve collectionId: explicit param > themeSetInfo.collectionId > 1 (only for creates)
    const resolvedCollectionId = collectionId
      ? parseInt(String(collectionId), 10)
      : (themeSetInfo.collectionId ? parseInt(String(themeSetInfo.collectionId), 10) : (isNew ? 1 : undefined));
    // API form expects products as integer IDs, not full objects
    const productIds = (themeSetInfo.products || []).map((p: any) => typeof p === 'object' ? parseInt(String(p.id), 10) : parseInt(String(p), 10));
    const body: Record<string, unknown> = { ...themeSetInfo, products: productIds };
    if (resolvedCollectionId !== undefined) {
      body['themeCollection'] = resolvedCollectionId;
      body['collectionId'] = resolvedCollectionId;
    }

    console.log('[ThemeService] saveThemeSet:', { id: themeSetInfo.id, collectionId: resolvedCollectionId, isNew });

    const request$ = isNew
      ? this.api.post<{ themeSet: ThemeSetInfo } | string>('/themes/sets', body)
      : this.api.put<{ themeSet: ThemeSetInfo } | string>('/themes/sets/' + themeSetInfo.id, body);

    return request$.pipe(
      map(result => {
        if (result && typeof result === 'object' && 'themeSet' in result) {
          return (result as { themeSet: ThemeSetInfo }).themeSet;
        }
        if (typeof result === 'string' && result.startsWith('ERROR_')) {
          console.error('[ThemeService] saveThemeSet returned error:', result);
        }
        return result as ThemeSetInfo | string;
      }),
      catchError(error => {
        console.error('[ThemeService] saveThemeSet error:', error);
        throw error;
      })
    );
  }

  /**
   * Get themes available for a specific product
   * GET /themes/:productId
   *
   * Returns 4 arrays:
   * - [0]: ThemeSetInfo[] - themes available for product
   * - [1]: ColorInfo[] - available colors for filtering
   * Backend endpoint: GET /api/products/{productId}/themes
   * Returns paginated published theme sets associated with the product.
   * Companion endpoints for filters:
   *   GET /api/products/{productId}/themes/colors
   *   GET /api/products/{productId}/themes/galleries
   *
   * @param productId - The product ID to get themes for
   * @param statusFilter - Unused (the endpoint only returns published themes), kept for API compat
   * @returns Observable<ProductThemesData> - Combined theme data
   */
  getProductThemes(productId: number, statusFilter: number = 2): Observable<ProductThemesData> {
    console.log('[ThemeService] getProductThemes:', { productId });

    // Fetch themes, colors, and galleries for this product in parallel
    return forkJoin({
      themesRes: this.api.get<any>('/products/' + productId + '/themes', { params: { limit: 100 }, suppressSessionExpiration: true }).pipe(
        catchError(err => { console.error('[ThemeService] getProductThemes themes error:', err); return of(null); })
      ),
      colorsRes: this.api.get<any>('/products/' + productId + '/themes/colors', { suppressSessionExpiration: true }).pipe(
        catchError(() => of([]))
      ),
      galleriesRes: this.api.get<any>('/products/' + productId + '/themes/galleries', { suppressSessionExpiration: true }).pipe(
        catchError(() => of([]))
      )
    }).pipe(
      map(({ themesRes, colorsRes, galleriesRes }) => {
        // Parse theme sets from the paginated response { items: [...], pagination: {...} }
        let themes: ThemeSetInfo[] = [];
        if (themesRes) {
          const items = Array.isArray(themesRes) ? themesRes : (themesRes.items || []);
          themes = items.map((set: any) => ({
            ...set,
            id: String(set.id),
            collectionId: this.resolveThemeSetCollectionId(set),
            collectionName: typeof set.collectionName === 'string'
              ? set.collectionName.trim()
              : (typeof set.themeCollection?.name === 'string' ? set.themeCollection.name.trim() : null),
            galleryId: set.galleryId !== null && set.galleryId !== undefined && set.galleryId !== ''
              ? String(set.galleryId)
              : null,
            status: String(set.status ?? 2),
            // Normalize theme variant IDs to strings
            themes: (set.themes || []).map((t: any) => ({ ...t, id: String(t.id) })),
          }));
        }

        // Parse colors (array of {id, color, name})
        const colors: ColorInfo[] = Array.isArray(colorsRes) ? colorsRes : (colorsRes?.items || []);

        // Parse galleries
        const galleries: ThemeGalleryInfo[] = Array.isArray(galleriesRes) ? galleriesRes : (galleriesRes?.items || []);

        // Extract distinct locales from all theme variants
        const localeSet = new Set<string>();
        for (const set of themes) {
          for (const t of (set.themes || [])) {
            if (t.locale) localeSet.add(t.locale);
          }
        }
        const locales: ThemeLocaleInfo[] = Array.from(localeSet).map(l => ({ locale: l, selected: false }));

        const themesData: ProductThemesData = { themes, colors, galleries, locales };
        console.log('[ThemeService] getProductThemes result:', {
          themes: themesData.themes.length,
          colors: themesData.colors.length,
          galleries: themesData.galleries.length,
          locales: themesData.locales.length,
          themesWithCollectionName: themesData.themes.filter(theme => !!theme.collectionName).length
        });
        return themesData;
      }),
      catchError(error => {
        console.error('[ThemeService] getProductThemes error:', error);
        return of({ themes: [], colors: [], galleries: [], locales: [] });
      })
    );
  }

  /**
   * Calculate MD5 hash for the bytes using SparkMD5
   * Returns a proper 32-character hex MD5 hash
   */
  private calculateMd5(buffer: Uint8Array): string {
    const spark = new SparkMD5.ArrayBuffer();
    spark.append(buffer.buffer as ArrayBuffer);
    return spark.end();
  }
}
